<template>
  <div class="antialiased">
    <PageCheck v-if="isVisualEditor" :bloks="content.blocks" />

    <!-- HEADER SECTION BEGIN -->
    <template v-if="siteHeader">
      <a
        href="#mainContent"
        class="bg-default absolute -top-96 z-50 rounded-br-lg px-2 py-1 font-medium text-white focus:top-0"
      >
        {{ skipNavigationLabel }}
      </a>
      <PageBlock :block="siteHeader" :context="context" />
    </template>
    <!-- HEADER SECTION END -->

    <main id="mainContent">
      <div
        v-if="
          !isVisualEditor && content.authenticationRequired && !store.userToken
        "
        class="h-96"
      >
        <span class="sr-only">Even wachten terwijl we u authenticeren</span>
        <LoadingState />
      </div>

      <PageBlock
        v-for="currentBlok in bodyBlocks"
        :key="currentBlok._uid"
        :block="currentBlok"
        :context="context"
      />
    </main>

    <!-- FOOTER SECTION BEGIN -->
    <PageBlock v-if="siteFooter" :block="siteFooter" :context="context" />
    <!-- FOOTER SECTION END -->

    <PageMetadataOverview v-if="isVisualEditor" :context="context" />
  </div>
</template>

<script setup>
import PageCheck from '../components/PageCheck.vue'
import PageBlock from '../components/PageBlock.vue'

import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useStoryblokBridge } from '@storyblok/vue'
import { useHead, useSeoMeta } from '@unhead/vue'
import { useMainStore } from '../store'
import PageMetadataOverview from '../components/PageMetadataOverview.vue'
import { getValidImageSrc } from '../utils/imgUtils'
import hydrateObject from '../utils/hydrateObject.js'
import { usePageContext } from '../renderer/usePageContext.js'
import LoadingState from '../components/LoadingState.vue'

const store = useMainStore()

const isVisualEditor = import.meta.env.VITE_IS_EDITOR

const content = ref({
  ...store.pageContent,
})

const context = computed(() => {
  return {
    ...store.pageContext,
    vacancy: store.vacancy,
    company: store.company,
    publication: store.publication,
    region: store.region,
    site: store.site,
    // This prop was renamed but we keep the old one to be backwards compatible,
    // otherwise we have to change old pages on Storyblok that were using it.
    publications: store.publication,
    currentYear: new Date().getFullYear(),
  }
})

const hydratedContent = computed(() => {
  // TODO Is this still needed?
  // Remove expired vacancy modal if it's present and we're not on editor mode and the vacancy is not expired
  // This is needed so Indeed doesn't mark our vacancies as closed
  // Extremely hacky solution, an alternative would be to implement an XML feed of our vacancies
  // for Indeed: https://developer.indeed.com/docs/indeed-apply/direct-employer/
  let blocks = content.value.blocks || []
  if (!isVisualEditor && context.value.publication) {
    const vacancyExpired =
      Date.now() > Date.parse(context.value.publication?.end)

    if (!vacancyExpired) {
      blocks = blocks.filter(
        (block) => block.component !== 'ExpiredVacancyTextWithCallToAction',
      )
    }
  }

  return hydrateObject(
    { ...content.value, blocks },
    context.value,
    isVisualEditor,
  )
})

const siteHeader = computed(() => {
  return hydratedContent.value.blocks.find(
    (item) => item.component === 'PageHeaderWithMenu',
  )
})
const siteFooter = computed(() => {
  return [...hydratedContent.value.blocks]
    .reverse()
    .find((item) => item.component.startsWith('Footer'))
})
const bodyBlocks = computed(() => {
  if (
    !isVisualEditor &&
    content.value.authenticationRequired &&
    !store.userToken
  ) {
    // If the page requires auth and the user is not logged in yet, we only load CSS blocks
    return hydratedContent.value.blocks.filter(
      (item) =>
        item._uid !== siteHeader.value?._uid &&
        item._uid !== siteFooter.value?._uid &&
        item.component === 'CustomCSS',
    )
  } else {
    return hydratedContent.value.blocks.filter(
      (item) =>
        item._uid !== siteHeader.value?._uid &&
        item._uid !== siteFooter.value?._uid,
    )
  }
})

const skipNavigationLabel = computed(() => {
  return siteHeader.value?.skipNavigationLabel || 'Ga direct naar de content'
})

const metadata = {
  ...hydratedContent.value,
}

const imgSourceTypes = []
metadata.openGraphImage && imgSourceTypes.push(metadata.openGraphImage)
metadata.openGraphImageUrl && imgSourceTypes.push(metadata.openGraphImageUrl)
metadata.image = getValidImageSrc(imgSourceTypes)

const seoMeta = {}
if (metadata.title) {
  seoMeta.twitterCard = 'summary'
  seoMeta.twitterTitle = metadata.title
  seoMeta.ogTitle = metadata.title

  useHead(
    {
      title: metadata.title,
    },
    { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
  )
}
if (metadata.description) {
  seoMeta.description = metadata.description
  seoMeta.twitterDescription = metadata.description
  seoMeta.ogDescription = metadata.description
}
if (store.blockRobots) {
  seoMeta.robots = 'noindex'
  if (store.pageContent?.slug.includes('/_default/_vacancy')) {
    seoMeta.robots = 'noindex, follow' // So it crawls the links of the recommended vacancies
  }
}
if (metadata.openGraphType) {
  seoMeta.ogType = metadata.openGraphType
}
if (metadata.image) {
  seoMeta.ogImage = metadata.image
}

// Execute this only on the server, so we can minimize client bundle
if (import.meta.env.SSR && seoMeta) {
  useSeoMeta(seoMeta)
}

// We generate the canonical URL without UTM params or params related to search
const pc = usePageContext()
const url = new URL(`https://${pc.hostname}${pc.urlOriginal}`)
// We mantain the page param in the canonical, so paginated pages are correctly indexed
const basicSearchParams = new URLSearchParams()
const page = url.searchParams.get('page')
if (page && page != 1) {
  basicSearchParams.set('page', url.searchParams.get('page'))
}
useHead({
  link: [
    {
      rel: 'canonical',
      href: `${url.origin}${url.pathname}${basicSearchParams.size ? '?' + basicSearchParams.toString() : ''}`,
    },
  ],
})

if (isVisualEditor) {
  onMounted(() => {
    useStoryblokBridge(content.value.id, (story) => {
      console.log('Updating visual editor!')

      // We replace the GlobalReferences with the blocks we already had on page load
      // (this 'story' object doesn't contain the referenced block data)
      const blocks = []
      story.content.blocks.forEach((block) => {
        if (block.component === 'GlobalReference') {
          const referencedBlocks = content.value.blocks.filter(
            (previousBlock) => previousBlock.reference === block._uid,
          )
          blocks.push(...referencedBlocks)
        } else {
          blocks.push(block)
        }
      })

      content.value = { ...story.content, blocks }
    })
  })
} else {
  onMounted(() => {
    setTimeout(() => {
      if (window.dataLayer) {
        const url = window.location.origin + window.location.pathname

        // We remove the lat and lng because Meta considers them personal info
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.delete('latitude')
        searchParams.delete('longitude')

        const event = {
          event: 'page_view', // Standard GA4 event name
          page_location: `${url}${searchParams.size ? '?' + searchParams.toString() : ''}`, // Full URL of the current page
          page_referrer: document.referrer, // Full URL of the previous page
          page_path: window.location.pathname, // Path without domain
          page_title: document.title, // Title of the page
        }

        if (window.dataLayer.some((e) => e.event === 'gtm.js')) {
          window.dataLayer.push(event)
        } else {
          // Listen for gtm.js
          const originalPush = window.dataLayer.push
          window.dataLayer.push = function () {
            const args = Array.from(arguments)
            if (args.some((e) => e.event === 'gtm.js')) {
              // Now safe to push your event
              originalPush.call(window.dataLayer, event)
              // Restore original push to avoid duplicate logic
              window.dataLayer.push = originalPush
            }
            return originalPush.apply(window.dataLayer, arguments)
          }
        }
      }
    }, 500)
  })
}

watch(context, async () => {
  // If the context changes, we refresh all the blocks, otherwise we get rendering issues
  content.value.blocks = []
  await nextTick()
  content.value.blocks = store.pageContent.blocks
})
</script>
